/**
 * Canonical enums shared across every surface. Declared as `as const` tuples so we get
 * both a runtime array (for validation/seeding/iteration) and a literal union type.
 */

export const PROMPT_TYPES = [
  'image',
  'video',
  'text',
  'code',
  'audio',
  'music',
  '3d',
  'design',
  'agent',
  'other',
] as const;
export type PromptType = (typeof PROMPT_TYPES)[number];

export const PROMPT_STATUSES = ['staging', 'published', 'rejected', 'archived'] as const;
export type PromptStatus = (typeof PROMPT_STATUSES)[number];

export const USER_ROLES = ['user', 'moderator', 'admin', 'owner'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Higher number = more privilege. */
export const ROLE_RANK: Record<UserRole, number> = {
  user: 0,
  moderator: 1,
  admin: 2,
  owner: 3,
};

export function hasRole(role: UserRole, atLeast: UserRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[atLeast];
}

export const SOURCE_TYPES = ['telegram', 'web', 'manual', 'user_submission'] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

export const MEDIA_TYPES = ['image', 'video', 'audio', 'text'] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];

export const COMMENT_STATUSES = ['visible', 'pending', 'hidden', 'deleted'] as const;
export type CommentStatus = (typeof COMMENT_STATUSES)[number];

export const FEED_SORTS = ['trending', 'new', 'top_rated', 'most_downloaded', 'source_popular'] as const; // prettier-ignore

/**
 * The platforms a reader can filter by.
 *
 * Canonical keys, not `source_name` values — `github.com` has to match 680 prompts stored under
 * three different repository paths, and `meigen.ai` is stored as a full URL.
 */
export const SOURCE_PLATFORMS = ['civitai.com', 'meigen.ai', 'youmind.com', 'promptden.com', 'github.com'] as const; // prettier-ignore
export type SourcePlatform = (typeof SOURCE_PLATFORMS)[number];
export type FeedSort = (typeof FEED_SORTS)[number];

export const INGESTION_KINDS = ['telegram_channel', 'website'] as const;
export type IngestionKind = (typeof INGESTION_KINDS)[number];

export const INGESTION_ITEM_STATUSES = [
  'new',
  // The claim stamped when `processIngestionBatch` takes an item, so a claim that never completes
  // can be told from one still running (`releaseStalledClaims` returns those to the queue). The
  // pipeline has written it since claiming was introduced; the contract did not list it, so any
  // consumer narrowing an item's status against this union was narrowing against a set the
  // database contradicts.
  'processing',
  'classified',
  'duplicate',
  'approved',
  'rejected',
  'error',
] as const;
export type IngestionItemStatus = (typeof INGESTION_ITEM_STATUSES)[number];

export const SUPPORT_THREAD_STATUSES = ['open', 'pending', 'closed'] as const;
export type SupportThreadStatus = (typeof SUPPORT_THREAD_STATUSES)[number];

export const SUPPORT_SENDERS = ['user', 'admin', 'system'] as const;
export type SupportSender = (typeof SUPPORT_SENDERS)[number];

export const BROADCAST_STATUSES = ['draft', 'scheduled', 'sending', 'sent', 'failed'] as const;
export type BroadcastStatus = (typeof BROADCAST_STATUSES)[number];

export const EVENT_TYPES = [
  'view',
  'search',
  'open_prompt',
  'copy',
  'share',
  'rate',
  'comment',
  'favorite',
  'download',
  'submit',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

// Limits / tunables (single source of truth).
export const RATING_MIN = 1;
export const RATING_MAX = 5;
export const COMMENT_MAX_LEN = 2000;
export const PROMPT_BODY_MAX_LEN = 20000;
export const SUBMIT_TITLE_MAX_LEN = 200;
export const DEFAULT_PAGE_SIZE = 24;
export const MAX_PAGE_SIZE = 100;

/** initData replay window (seconds). 24 h is standard for Mini Apps. */
export const INITDATA_TTL_SECONDS = 86400;

/** Free-tier daily download cap (enforced when monetization is enabled). */
export const FREE_DAILY_DOWNLOAD_CAP = 50;

/** Near-duplicate cosine threshold for ingestion dedupe. */
export const DEDUPE_SIMILARITY_THRESHOLD = 0.92;
